import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Logo, LogoProps } from '../Logo';
import { HeaderLinksDataProps } from '../Header';
import { useTheme } from '../../../../../theming/useTheme';
import { SecondaryLinks } from './SecondaryLinks';
import { secondaryHeaderHeight } from '../header.utils';
import { AnimationDirection, AnimationDirectionType } from '../menuAnimations.util';
import { ErrorBoundary } from '../../../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { createStyleForBoxShadow } from '../../../../../theming/boxShadow';
import { FontSizesStyles } from '../../../../../typography/semanticTags/semanticTags.utils';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';

const componentConfigThemeKey = 'secondaryHeader';

export const maxVisibleLinksOnLargeAndXLarge = 6;

export interface SecondaryHeaderDataProps {
  links: HeaderLinksDataProps[] | null
  logo: LogoProps | null
  moreLinksText: string,
  animation?: AnimationDirectionType,
  animationDuration?: number
  pagePath: string
}

export interface SecondaryHeaderThemeProps {
  baseUnit: BaseUnit
  backgroundColor: string
  boxShadowTypeNormal: string
  fontSizesStyles: FontSizesStyles
}

export type SecondaryHeaderProps = SecondaryHeaderDataProps & SecondaryHeaderThemeProps;

type SecondaryHeaderStyle = {
  header: object,
  foldingUp: object,
  foldingDown: object,
};

const getStyle = (baseUnit: BaseUnit, backgroundColor: string, animationDuration: number) => {
  return StyleSheet.create({
    header: {
      backgroundColor,
      position: 'relative',
      height: `${secondaryHeaderHeight}px`,
      display: 'grid',
      gridAutoFlow: 'column',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'flex-start',
      zIndex: 50,
      transition: `${animationDuration}ms`,
      gridColumnGap: `${baseUnit}px`,
      paddingLeft: `${baseUnit}px`,
    },
    foldingUp: {
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        transform: 'translateY(-100%)',
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        transform: 'translateY(-100%)',
      },
    },
    foldingDown: {
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        transform: 'translateY(0)',
        transitionDelay: '200ms',
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        transform: 'translateY(0)',
        transitionDelay: '200ms',
      },
    },
  });
};

const getClassWithAnimations = (animation: string, baseStyle: SecondaryHeaderStyle, boxShadow: { style: object }) => {
  switch (animation) {
    case AnimationDirection.UP: return css(baseStyle.header, boxShadow.style, baseStyle.foldingUp);
    case AnimationDirection.DOWN: return css(baseStyle.header, boxShadow.style, baseStyle.foldingDown);
    default: return css(baseStyle.header, boxShadow.style);
  }
};

export const SecondaryHeaderComponent: React.FunctionComponent<SecondaryHeaderProps> = props => {
  const { baseUnit, backgroundColor, boxShadowTypeNormal, links, logo, moreLinksText, animationDuration = 0, animation = AnimationDirection.NONE, pagePath } = props;
  const style = getStyle(baseUnit, backgroundColor, animationDuration);
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal });
  const finalStyle = getClassWithAnimations(animation, style, boxShadowStyle);
  return (
    <div className={finalStyle}>
      {logo ? <Logo {...logo} /> : null}
      {links ? <SecondaryLinks links={links} maxVisibleLinks={maxVisibleLinksOnLargeAndXLarge} moreLinksText={moreLinksText} pagePath={pagePath} /> : null}
    </div>
  );
};

const mapThemeProps = (theme: SecondaryHeaderThemeProps) => {
  return {
    baseUnit: theme.baseUnit,
    backgroundColor: theme.backgroundColor,
    boxShadowTypeNormal: theme.boxShadowTypeNormal,
    fontSizesStyles: theme.fontSizesStyles,
  };
};

export const SecondaryHeader = (props: SecondaryHeaderDataProps) => {
  const themeContext = useTheme(mapThemeProps, componentConfigThemeKey);
  return <ErrorBoundary><SecondaryHeaderComponent {...themeContext} {...props} /></ErrorBoundary>;
};
